import type {
  IModularBenefitBeneficiary,
  IModularBenefitContact,
} from '@ben/shared/types/modular-benefit';
import type { ISliderValue } from '../multi-select-options/multi-select-options';

const PENDING_ID_PREFIX = 'pending:';

// A dependant nominated from the picker is selected before their beneficiary
// record exists; the selection holds this placeholder until the record's id
// arrives.
export const toPendingBeneficiaryId = (contactId: number): string =>
  `${PENDING_ID_PREFIX}${contactId}`;

// Stored legacy selections hold the contact id as a number at runtime.
export const isPendingBeneficiaryId = (id: string): boolean =>
  typeof id === 'string' && id.startsWith(PENDING_ID_PREFIX);

export const contactName = ({
  firstName,
  lastName,
}: IModularBenefitContact): string => `${firstName} ${lastName}`;

// A selection entry is a legacy dependant reference when it is all digits;
// anything else (bar a pending placeholder) is a beneficiary record id.
const isLegacyContactId = (id: string): boolean => /^\d+$/.test(String(id));

// The dependant a selection entry stands for, whichever id form it uses:
// a legacy contact id, a pending placeholder, or a record via its linked
// contact. Undefined when the entry cannot be resolved.
const toSelectedContactId = (
  id: string,
  beneficiaries: IModularBenefitBeneficiary[]
): number | undefined => {
  if (isPendingBeneficiaryId(id)) {
    return Number(id.slice(PENDING_ID_PREFIX.length));
  }
  if (isLegacyContactId(id)) {
    return Number(id);
  }
  return beneficiaries.find((beneficiary) => beneficiary.id === id)
    ?.linkedContact;
};

const getSelectedContactIds = (
  values: ISliderValue[],
  beneficiaries: IModularBenefitBeneficiary[]
): number[] =>
  values
    .map(({ id }) => toSelectedContactId(id, beneficiaries))
    .filter((contactId): contactId is number => contactId !== undefined);

// Dependants offered for quick-add: not disabled, not already holding a
// beneficiary record (those are offered from the records list instead) and not
// already selected under any id form.
export const getAddableContacts = (
  contacts: IModularBenefitContact[],
  beneficiaries: IModularBenefitBeneficiary[],
  values: ISliderValue[]
): IModularBenefitContact[] => {
  const selectedContactIds = new Set(
    getSelectedContactIds(values, beneficiaries)
  );
  const linkedContactIds = new Set(
    beneficiaries.map(({ linkedContact }) => linkedContact)
  );

  return contacts.filter(
    ({ id, disabled }) =>
      !disabled && !linkedContactIds.has(id) && !selectedContactIds.has(id)
  );
};

// Existing beneficiary records offered for selection: not disabled and not
// already selected — by their own id or, through the linked dependant, by a
// legacy contact id.
export const getAddableBeneficiaries = (
  beneficiaries: IModularBenefitBeneficiary[],
  values: ISliderValue[]
): IModularBenefitBeneficiary[] => {
  const selectedContactIds = new Set(
    getSelectedContactIds(values, beneficiaries)
  );

  return beneficiaries.filter(
    ({ disabled, linkedContact }) =>
      !disabled && !selectedContactIds.has(linkedContact)
  );
};

export interface NomineeDetails {
  name: string;
  relationshipLabel: string;
  disabled: boolean;
}

// The person a selection entry refers to: a record by its id, or a dependant
// by its legacy contact id or pending placeholder. Null when the entry matches
// neither list, so the caller can still render a row for it.
export const resolveNominee = (
  id: string,
  contacts: IModularBenefitContact[],
  beneficiaries: IModularBenefitBeneficiary[]
): NomineeDetails | null => {
  const beneficiary = beneficiaries.find((row) => row.id === id);
  if (beneficiary) {
    return {
      name: beneficiary.name,
      relationshipLabel: beneficiary.relationshipLabel,
      disabled: beneficiary.disabled,
    };
  }
  const contactId = toSelectedContactId(id, beneficiaries);
  const contact = contacts.find((row) => row.id === contactId);
  return contact
    ? {
        name: contactName(contact),
        relationshipLabel: contact.contactType,
        disabled: contact.disabled,
      }
    : null;
};

// True when two selection entries stand for the same dependant under
// different id forms (a legacy contact id alongside that person's record).
export const hasDuplicateNominee = (
  values: ISliderValue[],
  beneficiaries: IModularBenefitBeneficiary[]
): boolean => {
  const contactIds = getSelectedContactIds(values, beneficiaries);
  return new Set(contactIds).size !== contactIds.length;
};
